import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { APP_FONT_FAMILY, fontScale } from '@/theme';

export const usePeoplePickerStyles = makeStyles({
  root: {
    position: 'relative',
    width: '100%',
    paddingTop: tokens.spacingVerticalXXS,
  },
  rootOpen: {
    zIndex: 10,
  },
  inputArea: {
    position: 'relative',
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    // Spec 099 FR-012: MS FTE Sponsor input MUST render the same visible
    // border as other Step 1 inputs. Match the Fluent UI `Input` bottom-line
    // treatment via colorNeutralStrokeAccessible.
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    borderBottomColor: tokens.colorNeutralStrokeAccessible,
    outline: 'none',
    boxShadow: 'none',
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '2.5rem',
    ...shorthands.padding('0', tokens.spacingHorizontalM),
    cursor: 'text',
    marginBottom: '4px',
    '&:focus-within': {
      borderBottomColor: tokens.colorBrandStroke1,
      borderBottomWidth: '2px',
    },
  },
  inputWrapperDisabled: {
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled),
    cursor: 'not-allowed',
  },
  input: {
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    flexGrow: 1,
    fontFamily: APP_FONT_FAMILY,
    fontSize: fontScale.subtitle,
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
    ...shorthands.padding(tokens.spacingVerticalS, '0'),
    '&::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
    '&:disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalS),
    fontSize: fontScale.body,
    lineHeight: tokens.lineHeightBase300,
    maxWidth: '100%',
  },
  pillRemove: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.border('none'),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    ...shorthands.padding('0'),
    color: tokens.colorNeutralForeground2,
    '&:hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  dropdown: {
    position: 'absolute',
    top: `calc(100% + ${tokens.spacingVerticalXXS})`,
    left: '0',
    right: '0',
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    boxShadow: tokens.shadow16,
    maxHeight: '20rem',
    overflowY: 'auto',
  },
  dropdownMessage: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    color: tokens.colorNeutralForeground3,
    fontSize: fontScale.body,
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    cursor: 'pointer',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  resultItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  resultInfo: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('hidden'),
    flexGrow: 1,
  },
  resultName: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  resultDetail: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  avatar: {
    width: '2.25rem',
    height: '2.25rem',
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
    objectFit: 'cover',
  },
  label: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
    display: 'block',
  },
});
